<template>
  <div class="edit-message-page">
    <v-container class="py-8">
      <!-- Header -->
      <div class="page-header mb-3">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Edit Message
            </v-card-title>

            <v-card-text class="pa-6">
              <!-- Introduction Text -->
              <div class="mb-6">
                <p class="text-h6 mb-4">
                  Update your personal message to encourage those who knew {{ memorial?.nameOfPerson || 'your loved one' }} to share their photos and stories.
                </p>
              </div>

              <!-- Recording Options -->
              <div class="mb-6">
                <h3 class="text-h5 mb-4">Choose Your Message Option</h3>

                <v-card class="option-card mb-4" elevation="0">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <h4 class="text-h6 mb-2">Record a Video Message</h4>
                        <p class="text-body-2 text--secondary">
                          Record a personal video message that will be shown to family and friends
                        </p>
                      </div>
                      <v-btn
                        color="primary"
                        @click="startVideoRecording"
                        :disabled="isRecording"
                      >
                        {{ recordButtonLabel }}
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <v-card class="option-card" elevation="0">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <h4 class="text-h6 mb-2">Write a Text Message</h4>
                        <p class="text-body-2 text--secondary">
                          Write a personal message that will be displayed to family and friends
                        </p>
                      </div>
                      <v-btn
                        color="secondary"
                        variant="outlined"
                        @click="chooseTextMessage"
                      >
                        Write Message
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Video Recording Interface -->
              <v-expand-transition>
                <div v-if="showVideoRecording" class="mb-6">
                  <v-card class="recording-card" elevation="2">
                    <v-card-title class="pb-2">
                      <v-icon left color="error">mdi-video</v-icon>
                      Video Recording
                    </v-card-title>

                    <v-card-text>
                      <div class="video-recording-container">
                        <video
                          ref="videoPreview"
                          class="video-preview"
                          :class="{ 'recording': isRecording }"
                          autoplay
                          :muted="isRecording || !recordedVideo"
                          playsinline
                          controls
                          style="width: 100%; max-width: 600px; border-radius: 8px; background: var(--v-theme-black);"
                        ></video>

                        <div class="recording-controls mt-4">
                          <v-btn
                            v-if="!isRecording && !recordedVideo"
                            color="error"
                            size="large"
                            @click="startRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-record</v-icon>
                            Start Recording
                          </v-btn>

                          <v-btn
                            v-if="isRecording"
                            color="error"
                            size="large"
                            @click="stopRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-stop</v-icon>
                            Stop Recording
                          </v-btn>

                          <v-btn
                            v-if="recordedVideo"
                            color="primary"
                            @click="playRecording"
                            class="mr-4"
                          >
                            <v-icon left>mdi-play</v-icon>
                            Review
                          </v-btn>

                          <v-btn
                            v-if="recordedVideo"
                            color="secondary"
                            variant="outlined"
                            @click="retakeRecording"
                          >
                            <v-icon left>mdi-refresh</v-icon>
                            Re-record
                          </v-btn>
                        </div>

                        <div v-if="isRecording" class="recording-indicator mt-2">
                          <v-chip color="error">
                            <v-icon left>mdi-circle</v-icon>
                            Recording {{ recordingTime }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Text Message Interface -->
              <v-expand-transition>
                <div v-if="showTextMessage">
                  <v-card class="text-message-card" elevation="2">
                    <v-card-title class="pb-2">
                      <v-icon left color="primary">mdi-message-text</v-icon>
                      Personal Message
                    </v-card-title>

                    <v-card-text class="mt-3">
                      <v-textarea
                        v-model="personalMessage"
                        label="Write your personal message here..."
                        variant="outlined"
                        rows="6"
                        auto-grow
                        placeholder="Share why this memorial is important to you and encourage others to contribute their own memories..."
                      ></v-textarea>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="cancel"
                class="mr-4"
              >
                Back to Dashboard
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="!canSave || saving"
                :loading="saving"
                @click="saveMessage"
                class="px-8"
              >
                Save
                <v-icon right class="ml-2">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Permission Dialog -->
    <v-dialog v-model="showPermissionDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white pa-4">
          <v-icon left color="white">mdi-video-off</v-icon>
          Camera/Microphone Access Required
        </v-card-title>
        <v-card-text class="pa-6">
          <p style="white-space: pre-line;">{{ permissionInstructions }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="showPermissionDialog = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white pa-4">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Error Saving Message
        </v-card-title>
        <v-card-text class="pa-6">
          <p>{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="showErrorDialog = false"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import ProfileMenu from '../components/ProfileMenu.vue'

const router = useRouter()
const store = useMemorialStore()

// Refs
const videoPreview = ref(null)
const memorial = ref(null)

// State
const showVideoRecording = ref(false)
const showTextMessage = ref(false)
const isRecording = ref(false)
const recordedVideo = ref(null)
const personalMessage = ref('')
const recordingTime = ref('00:00')
const mediaRecorder = ref(null)
const mediaStream = ref(null)
const saving = ref(false)
const showPermissionDialog = ref(false)
const permissionInstructions = ref('')
const showErrorDialog = ref(false)
const errorMessage = ref('')

const currentVideoUrl = ref('')
const currentTextMessage = ref('')

// Recording timer
let recordingTimer = null
let recordingStartTime = 0

// Computed
const canSave = computed(() => {
  return (recordedVideo.value !== null) || (personalMessage.value && personalMessage.value.trim().length > 0)
})

const recordButtonLabel = computed(() => {
  if (isRecording.value) {
    return 'Recording...'
  }
  return 'Record Video'
})

// Methods
const loadMemorialData = async () => {
  try {
    const response = await apiService.getMemorial(store.memorialUuid)
    if (response.success && response.memorial) {
      memorial.value = response.memorial

      // Load current message - prioritize video over text
      if (response.memorial.hostVideoMessage) {
        const video = response.memorial.hostVideoMessage
        currentVideoUrl.value = `${video.location}/${video.filename}.${video.extension}`

        // Show the video as if it was just recorded
        showVideoRecording.value = true
        showTextMessage.value = false
        recordedVideo.value = {
          blob: null, // Server video doesn't have blob
          url: currentVideoUrl.value,
          isServerVideo: true // Mark as server video so we don't try to upload it again
        }

        // Display the video in preview (don't autoplay)
        setTimeout(() => {
          if (videoPreview.value && recordedVideo.value) {
            videoPreview.value.srcObject = null
            videoPreview.value.src = recordedVideo.value.url
            videoPreview.value.pause()
          }
        }, 100)
      } else if (response.memorial.hostMessage) {
        currentTextMessage.value = response.memorial.hostMessage
        personalMessage.value = response.memorial.hostMessage
        // Automatically expand text message section if there's an existing text message
        showTextMessage.value = true
      }
    }
  } catch (err) {
    console.error('Error loading memorial:', err)
  }
}

const startVideoRecording = async () => {
  showVideoRecording.value = true
  showTextMessage.value = false
  personalMessage.value = ''

  try {
    // Check if permissions API is available
    if (navigator.permissions) {
      try {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' })
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' })

        if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
          showPermissionDeniedMessage()
          return
        }
      } catch (permError) {
        // Permissions API might not support camera/microphone on all browsers
        console.log('Permissions API not fully supported, proceeding with getUserMedia')
      }
    }

    mediaStream.value = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true
    })
    videoPreview.value.srcObject = mediaStream.value
  } catch (error) {
    console.error('Error accessing camera:', error)

    // Check if it's a permission error
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      showPermissionDeniedMessage()
    } else {
      // Other error (no camera available, etc.)
      permissionInstructions.value = 'Unable to access camera or microphone. Please check that your device has these features and try again.'
      showPermissionDialog.value = true
      showVideoRecording.value = false
    }
  }
}

const showPermissionDeniedMessage = () => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
  const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor)
  const isFirefox = /Firefox/.test(navigator.userAgent)

  let instructions = ''

  if (isMac && isSafari) {
    instructions = 'Safari on Mac:\n1. Click Safari menu > Settings\n2. Go to Websites > Camera and Microphone\n3. Allow access for this website\n4. Refresh the page and try again'
  } else if (isMac && isChrome) {
    instructions = 'Chrome on Mac:\n1. Click the lock icon in the address bar\n2. Change Camera and Microphone permissions to "Allow"\n3. Refresh the page and try again'
  } else if (isChrome) {
    instructions = 'Chrome:\n1. Click the lock or camera icon in the address bar\n2. Change Camera and Microphone permissions to "Allow"\n3. Refresh the page and try again'
  } else if (isFirefox) {
    instructions = 'Firefox:\n1. Click the camera icon in the address bar\n2. Remove the blocked permissions\n3. Refresh the page and try again'
  } else {
    instructions = 'Please check your browser settings to allow camera and microphone access for this website, then refresh and try again.'
  }

  permissionInstructions.value = `Camera and microphone access denied.\n\n${instructions}\n\nAlternatively, you can close this dialog and choose to write a text message instead.`
  showPermissionDialog.value = true
  showVideoRecording.value = false
}

const chooseTextMessage = () => {
  showTextMessage.value = true
  showVideoRecording.value = false
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop())
    mediaStream.value = null
  }
  recordedVideo.value = null
}

const startRecording = () => {
  if (!mediaStream.value) return

  const chunks = []

  // Configure MediaRecorder with options to ensure audio is captured
  let options = { mimeType: 'video/webm;codecs=vp8,opus' }

  // Fallback to default if the specified mimeType is not supported
  if (!MediaRecorder.isTypeSupported(options.mimeType)) {
    console.log('vp8,opus not supported, trying vp9,opus')
    options = { mimeType: 'video/webm;codecs=vp9,opus' }

    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.log('vp9,opus not supported, using default')
      options = { mimeType: 'video/webm' }
    }
  }

  mediaRecorder.value = new MediaRecorder(mediaStream.value, options)

  mediaRecorder.value.ondataavailable = (event) => {
    if (event.data.size > 0) {
      chunks.push(event.data)
    }
  }

  mediaRecorder.value.onstop = () => {
    const blob = new Blob(chunks, { type: 'video/webm' })
    recordedVideo.value = {
      blob,
      url: URL.createObjectURL(blob)
    }

    // Automatically play the recorded video for review
    setTimeout(() => {
      playRecording()
    }, 100)
  }

  mediaRecorder.value.start()
  isRecording.value = true
  recordingStartTime = Date.now()

  recordingTimer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - recordingStartTime) / 1000)
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0')
    const seconds = (elapsed % 60).toString().padStart(2, '0')
    recordingTime.value = `${minutes}:${seconds}`
  }, 1000)
}

const stopRecording = () => {
  if (mediaRecorder.value && isRecording.value) {
    mediaRecorder.value.stop()
    isRecording.value = false

    if (recordingTimer) {
      clearInterval(recordingTimer)
      recordingTimer = null
    }
  }
}

const playRecording = () => {
  if (recordedVideo.value && videoPreview.value) {
    videoPreview.value.srcObject = null
    videoPreview.value.src = recordedVideo.value.url
    videoPreview.value.muted = false // Ensure audio is enabled for playback
    videoPreview.value.play().catch(err => {
      console.error('Error playing recorded video:', err)
    })
  }
}

const retakeRecording = async () => {
  if (recordedVideo.value?.url && recordedVideo.value.url.startsWith('blob:')) {
    URL.revokeObjectURL(recordedVideo.value.url)
  }
  recordedVideo.value = null
  recordingTime.value = '00:00'

  // If we don't have a media stream (e.g., loaded from server), request camera access
  if (!mediaStream.value) {
    try {
      // Check permissions first
      if (navigator.permissions) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' })
          const microphonePermission = await navigator.permissions.query({ name: 'microphone' })

          if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
            showPermissionDeniedMessage()
            return
          }
        } catch (permError) {
          console.log('Permissions API not fully supported, proceeding with getUserMedia')
        }
      }

      mediaStream.value = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      })
      videoPreview.value.srcObject = mediaStream.value
    } catch (error) {
      console.error('Error accessing camera:', error)
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        showPermissionDeniedMessage()
      } else {
        permissionInstructions.value = 'Unable to access camera or microphone. Please check that your device has these features and try again.'
        showPermissionDialog.value = true
      }
    }
  } else {
    // Already have stream, just reconnect it
    videoPreview.value.srcObject = mediaStream.value
  }
}

const saveMessage = async () => {
  saving.value = true

  try {
    // If video was recorded (not just loaded from server), upload it
    if (recordedVideo.value && recordedVideo.value.blob && !recordedVideo.value.isServerVideo) {
      const videoFile = new File([recordedVideo.value.blob], 'host-message.webm', { type: 'video/webm' })

      const uploadResponse = await apiService.uploadVideoMessage(
        store.memorialUuid,
        videoFile,
        'Host Message',
        store.apiToken
      )

      if (!uploadResponse.success) {
        throw new Error(uploadResponse.msg || 'Failed to upload video')
      }

      // Upload already saved the video relationship, so we're done
      console.log('Video message saved successfully')
      router.push('/memory/dashboard')
      return
    }

    // If we have a server video and no new recording, just navigate back (no changes needed)
    if (recordedVideo.value && recordedVideo.value.isServerVideo) {
      console.log('No changes to existing video')
      router.push('/memory/dashboard')
      return
    }

    // Only update text message if no video
    if (!recordedVideo.value && personalMessage.value) {
      const updateResponse = await apiService.editMemorial({
        uuid: store.memorialUuid,
        hostMessage: personalMessage.value.slice(0, 10000)
      }, store.apiToken)

      if (!updateResponse.success) {
        throw new Error(updateResponse.msg || 'Failed to save message')
      }

      console.log('Text message saved successfully')
      router.push('/memory/dashboard')
    } else {
      // No video and no text - shouldn't reach here due to canSave check
      router.push('/memory/dashboard')
    }
  } catch (err) {
    console.error('Error saving message:', err)
    errorMessage.value = err.message || 'An error occurred while saving. Please try again.'
    showErrorDialog.value = true
  } finally {
    saving.value = false
  }
}

const cancel = () => {
  router.push('/memory/dashboard')
}

// Cleanup
const cleanup = () => {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop())
  }
  if (recordingTimer) {
    clearInterval(recordingTimer)
  }
  if (recordedVideo.value?.url && recordedVideo.value.url.startsWith('blob:')) {
    URL.revokeObjectURL(recordedVideo.value.url)
  }
}

onMounted(() => {
  loadMemorialData()
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.edit-message-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.main-card {
  border-radius: 12px;
  overflow: hidden;
}

.option-card {
  border: 2px solid var(--v-theme-neutral200);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.option-card:hover {
  border-color: var(--v-theme-accent);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.recording-card,
.text-message-card,
.current-message-card {
  border-radius: 8px;
}

.video-preview.recording {
  border: 3px solid var(--v-theme-recordingStrong);
  animation: pulse-border 1.5s ease-in-out infinite;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: var(--v-theme-recordingStrong);
  }
  50% {
    border-color: var(--v-theme-recordingLight);
  }
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}
</style>
